import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import {
  dismissNotificationPrompt,
  getNotificationPermission,
  getNotificationSupportInfo,
  isLikelyIOS,
  isStandalonePwa,
  requestNotificationPermission,
  wasNotificationPromptDismissed,
} from "../utils/notificationPermission";
import { subscribeToPush } from "../utils/pushSubscription";
import { useTheme } from "../contexts/ThemeContext";

const fontHeading = '"Poppins", "Inter", sans-serif';
const fontBody = '"Inter", "Poppins", sans-serif';

/**
 * Banner para pedir permissão de notificações — alinhado ao GIO-VISUAL-STUDY.
 */
export default function NotificationPermissionPrompt() {
  const { themeMode } = useTheme();
  const isDark = themeMode === "dark";
  const isMobile = useMediaQuery("(max-width:600px)");
  const [visible, setVisible] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    const info = getNotificationSupportInfo();
    if (!info.supported) return;
    if (wasNotificationPromptDismissed()) return;
    if (getNotificationPermission() !== "default") return;
    setVisible(true);
  }, []);

  const handleDismiss = useCallback(() => {
    dismissNotificationPrompt();
    setVisible(false);
  }, []);

  const handleEnable = useCallback(async () => {
    setRequesting(true);
    try {
      const result = await requestNotificationPermission();
      if (result.state === "granted") {
        setVisible(false);
        subscribeToPush().catch(() => {});
      } else if (result.state === "denied") {
        setVisible(false);
        dismissNotificationPrompt();
      }
    } finally {
      setRequesting(false);
    }
  }, []);

  if (!visible) return null;

  const ios = isLikelyIOS();
  const standalone = isStandalonePwa();

  const cardSx = isDark
    ? {
        background: "linear-gradient(135deg, #1e1b3a 0%, rgba(91,89,214,0.08) 100%)",
        boxShadow:
          "0 4px 6px -1px rgba(91,89,214,0.2), 0 2px 4px -1px rgba(91,89,214,0.15), 0 25px 50px -12px rgba(91,89,214,0.2)",
        border: "1px solid rgba(91,89,214,0.2)",
        backdropFilter: "blur(12px)",
      }
    : {
        backgroundColor: "#ffffff",
        boxShadow:
          "0 1px 3px 0 rgba(105,103,251,0.1), 0 1px 2px 0 rgba(105,103,251,0.06), 0 25px 50px -12px rgba(105,103,251,0.15)",
        border: "1px solid rgba(105,103,251,0.1)",
        backdropFilter: "blur(12px)",
      };

  const titleColor = isDark ? "#ffffff" : "#1a202c";
  const bodyColor = isDark ? "rgba(255,255,255,0.8)" : "#4a5568";
  const mutedColor = isDark ? "rgba(255,255,255,0.55)" : "#64748b";

  const containedBtnSx = {
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 600,
    fontFamily: fontBody,
    px: 2.5,
    py: 1,
    background: "linear-gradient(135deg, #8b89e3 0%, #9b9fe5 100%)",
    boxShadow: isDark
      ? "0 4px 12px rgba(91,89,214,0.25)"
      : "0 4px 12px rgba(91,89,214,0.2)",
    color: "#ffffff",
    "&:hover": {
      background: "linear-gradient(135deg, #5b59d6 0%, #6b6fd8 100%)",
      boxShadow: isDark
        ? "0 6px 15px rgba(91,89,214,0.35)"
        : "0 6px 15px rgba(91,89,214,0.3)",
    },
    "&:disabled": {
      background: "linear-gradient(135deg, rgba(139,137,227,0.5) 0%, rgba(155,159,229,0.5) 100%)",
      color: "rgba(255,255,255,0.7)",
    },
  };

  const textBtnSx = {
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 600,
    fontFamily: fontBody,
    color: isDark ? "rgba(255,255,255,0.85)" : "#5b59d6",
    "&:hover": {
      backgroundColor: isDark ? "rgba(91,89,214,0.12)" : "rgba(91,89,214,0.06)",
    },
  };

  return (
    <Box
      role="dialog"
      aria-label="Ativar notificações"
      sx={{
        position: "fixed",
        left: { xs: 16, sm: 24 },
        right: { xs: 16, sm: 24 },
        top: { xs: "calc(12px + env(safe-area-inset-top, 0px))", sm: "calc(16px + env(safe-area-inset-top, 0px))" },
        zIndex: (theme) => theme.zIndex.snackbar + 1,
        maxWidth: 480,
        mx: "auto",
        borderRadius: "16px",
        overflow: "hidden",
        ...cardSx,
        "@keyframes gioNotifPromptIn": {
          "0%": { opacity: 0, transform: "translateY(-16px) scale(0.98)" },
          "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
        },
        animation: "gioNotifPromptIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards",
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          opacity: 1,
          transform: "none",
        },
      }}
    >
      {/* Faixa superior — gradiente marca (ref. badges / header) */}
      <Box
        sx={{
          height: 3,
          width: "100%",
          background: "linear-gradient(90deg, #6967FB 0%, #C8F904 100%)",
          opacity: isDark ? 0.95 : 0.9,
        }}
      />

      <Box sx={{ position: "relative", p: { xs: 2, sm: 2.5 }, pt: { xs: 1.75, sm: 2 } }}>
        <IconButton
          size="small"
          aria-label="Fechar"
          onClick={handleDismiss}
          disabled={requesting}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
            color: mutedColor,
            "&:hover": {
              backgroundColor: isDark ? "rgba(91,89,214,0.15)" : "rgba(105,103,251,0.08)",
              color: titleColor,
            },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          sx={{ pr: { xs: 4, sm: 5 } }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "14px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isDark
                ? "linear-gradient(135deg, rgba(105,103,251,0.28) 0%, rgba(200,249,4,0.12) 100%)"
                : "linear-gradient(135deg, rgba(105,103,251,0.14) 0%, rgba(168,201,4,0.1) 100%)",
              border: isDark
                ? "1px solid rgba(91,89,214,0.35)"
                : "1px solid rgba(105,103,251,0.2)",
              boxShadow: isDark
                ? "0 2px 8px rgba(91,89,214,0.15)"
                : "0 2px 8px rgba(105,103,251,0.08)",
            }}
          >
            <NotificationsActiveRoundedIcon
              sx={{
                fontSize: 28,
                color: isDark ? "#C8F904" : "#5b59d6",
              }}
              aria-hidden
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: fontHeading,
                fontWeight: 700,
                fontSize: { xs: "1rem", sm: "1.0625rem" },
                letterSpacing: "-0.02em",
                lineHeight: 1.35,
                color: titleColor,
                mb: 0.75,
              }}
            >
              Fique por dentro em tempo real
            </Typography>
            <Typography
              sx={{
                fontFamily: fontBody,
                fontSize: "0.875rem",
                lineHeight: 1.55,
                color: bodyColor,
                mb: ios && !standalone ? 1.25 : 1.75,
              }}
            >
              Ative as notificações para receber avisos importantes no navegador, no Android
              ou no iPhone — sempre alinhado ao que você já usa no GIO BRAIN.
            </Typography>

            {ios && !standalone && (
              <Box
                sx={{
                  mb: 1.75,
                  py: 1,
                  px: 1.25,
                  borderRadius: "10px",
                  backgroundColor: isDark ? "rgba(91,89,214,0.12)" : "rgba(105,103,251,0.06)",
                  border: isDark
                    ? "1px solid rgba(91,89,214,0.18)"
                    : "1px solid rgba(105,103,251,0.12)",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: fontBody,
                    fontSize: "0.75rem",
                    lineHeight: 1.5,
                    color: mutedColor,
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 600, color: isDark ? "#a8c904" : "#5b59d6" }}>
                    iPhone / iPad:
                  </Box>{" "}
                  use &quot;Adicionar à Tela de Início&quot; no Safari para o melhor suporte (iOS
                  16.4+).
                </Typography>
              </Box>
            )}

            <Stack
              direction={isMobile ? "column-reverse" : "row"}
              spacing={1}
              alignItems={isMobile ? "stretch" : "center"}
              sx={{ flexWrap: "wrap", gap: 1 }}
            >
              <Button
                variant="contained"
                disableElevation
                disabled={requesting}
                onClick={handleEnable}
                fullWidth={isMobile}
                sx={containedBtnSx}
              >
                {requesting ? "Aguardando permissão…" : "Permitir notificações"}
              </Button>
              <Button
                variant="text"
                disabled={requesting}
                onClick={handleDismiss}
                fullWidth={isMobile}
                sx={textBtnSx}
              >
                Agora não
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
